import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Save,
  Cancel,
  Download,
  PictureAsPdf,
  Redo,
} from "@mui/icons-material";

import useClientInvoice from "../../hooks/useClientInvoice";
import useThirdPartyInvoice from "../../hooks/useThirdPartyInvoice";
import Dialog_YesNo from "../ui/Dialog_YesNo";
import InvoiceAuditHistoryTab from "./InvoiceAuditHistoryTab";

const InvoiceDetailDialog = ({ open, invoice, onClose, onUpdate }) => {
  const {
    addLine,
    updateLine,
    deleteLine,
    exportCSV,
    generatePDF,
    resendInvoice,
  } = useClientInvoice();

  const { getInvoiceByClientProject } = useThirdPartyInvoice();

  const [editingLineId, setEditingLineId] = useState(null);
  const [addingLine, setAddingLine] = useState(false);
  const [deleteLineId, setDeleteLineId] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [thirdPartyInvoices, setThirdPartyInvoices] = useState([]);
  const [selectedThirdPartyLines, setSelectedThirdPartyLines] = useState(
    new Set(),
  );
  const [lineForm, setLineForm] = useState({
    description: "",
    quantity: 1,
    rate: 0,
    taxExempt: false,
  });

  // Load third-party invoices when dialog opens or invoice changes
  useEffect(() => {
    if (open && invoice?.clientId && invoice?.projectId) {
      (async () => {
        const result = await getInvoiceByClientProject(
          invoice.clientId,
          invoice.projectId,
        );
        if (result && result.lines) {
          setThirdPartyInvoices(result.lines);
        } else {
          setThirdPartyInvoices([]);
        }
      })();
    }
  }, [open, invoice?.id, invoice?.clientId, invoice?.projectId]);

  if (!invoice) return null;

  const isEditable = invoice.status !== "sent";

  const handleStartAdd = () => {
    setLineForm({
      description: "",
      quantity: 1,
      rate: 0,
      taxExempt: false,
    });
    setAddingLine(true);
  };

  const handleStartEdit = (line) => {
    setLineForm({
      description: line.description,
      quantity: line.quantity,
      rate: line.rate,
      taxExempt: line.taxExempt,
    });
    setEditingLineId(line._id);
  };

  const handleSaveAdd = async () => {
    const line = {
      type: "other",
      description: lineForm.description,
      quantity: parseFloat(lineForm.quantity) || 1,
      rate: parseFloat(lineForm.rate) || 0,
      taxExempt: lineForm.taxExempt,
    };

    const result = await addLine(invoice.id, line);
    if (result) {
      setAddingLine(false);
      setLineForm({ description: "", quantity: 1, rate: 0, taxExempt: false });
      if (onUpdate) onUpdate();
    }
  };

  const handleSaveEdit = async () => {
    const line = {
      description: lineForm.description,
      quantity: parseFloat(lineForm.quantity) || 1,
      rate: parseFloat(lineForm.rate) || 0,
      taxExempt: lineForm.taxExempt,
    };

    const result = await updateLine(invoice.id, editingLineId, line);
    if (result) {
      setEditingLineId(null);
      setLineForm({ description: "", quantity: 1, rate: 0, taxExempt: false });
      if (onUpdate) onUpdate();
    }
  };

  const handleCancelEdit = () => {
    setEditingLineId(null);
    setAddingLine(false);
    setLineForm({ description: "", quantity: 1, rate: 0, taxExempt: false });
  };

  const handleDeleteLine = async () => {
    if (!deleteLineId) return;
    const result = await deleteLine(invoice.id, deleteLineId);
    if (result) {
      setDeleteLineId(null);
      if (onUpdate) onUpdate();
    }
  };

  const handleCopyThirdPartyLines = async () => {
    const linesToCopy = Array.from(selectedThirdPartyLines).map((idx) => {
      const line = thirdPartyInvoices[idx];
      return {
        type: "third-party",
        description: line.description,
        quantity: 1,
        rate: line.amount,
        taxExempt: line.taxExempt,
      };
    });

    // Add each line to the invoice
    for (const line of linesToCopy) {
      await addLine(invoice.id, line);
    }

    // Clear selection and refresh
    setSelectedThirdPartyLines(new Set());
    if (onUpdate) onUpdate();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "warning";
      case "approved":
        return "info";
      case "sent":
        return "success";
      default:
        return "default";
    }
  };

  const handleResendInvoice = async () => {
    const result = await resendInvoice(invoice.id);
    if (result) {
      if (onUpdate) onUpdate();
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{invoice.invoiceNumber}</DialogTitle>
        <DialogContent sx={{ paddingTop: 2 }}>
          {/* Tabs for content */}
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
            >
              <Tab label="Invoice Details" />
              <Tab label="Third-Party Invoices" />
              <Tab label="Audit History" />
            </Tabs>
          </Box>

          {/* Tab 0: Invoice Details */}
          {activeTab === 0 && (
            <Box
              display="flex"
              flexDirection="column"
              gap={2}
            >
              {/* Header info */}
              <Box
                display="grid"
                gridTemplateColumns="1fr 1fr"
                gap={2}
              >
                <Box>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                  >
                    Client
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                  >
                    {invoice.client?.name}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                  >
                    Project
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                  >
                    {invoice.project?.name}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                  >
                    Invoice Date
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                  >
                    {new Date(invoice.invoiceDate).toLocaleDateString()}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                  >
                    Status
                  </Typography>
                  <Chip
                    label={invoice.status}
                    color={getStatusColor(invoice.status)}
                    size="small"
                  />
                </Box>
              </Box>

              {/* Line items table */}
              <Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  marginBottom={1}
                >
                  <Typography variant="subtitle2">Line Items</Typography>
                  {isEditable && !addingLine && !editingLineId && (
                    <Button
                      size="small"
                      startIcon={<Add />}
                      onClick={handleStartAdd}
                    >
                      Add Line
                    </Button>
                  )}
                </Box>

                <Table
                  size="small"
                  sx={{ border: "1px solid #ddd" }}
                >
                  <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Rate</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      {isEditable && (
                        <TableCell align="center">Actions</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoice.lines?.map((line) => {
                      const isEditing = editingLineId === line._id;
                      const canEdit = line.type === "other" && isEditable;

                      return (
                        <TableRow key={line._id}>
                          {isEditing ? (
                            <>
                              <TableCell>
                                <TextField
                                  size="small"
                                  fullWidth
                                  value={lineForm.description}
                                  onChange={(e) =>
                                    setLineForm({
                                      ...lineForm,
                                      description: e.target.value,
                                    })
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  type="number"
                                  value={lineForm.quantity}
                                  onChange={(e) =>
                                    setLineForm({
                                      ...lineForm,
                                      quantity: e.target.value,
                                    })
                                  }
                                  sx={{ width: 80 }}
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  type="number"
                                  value={lineForm.rate}
                                  onChange={(e) =>
                                    setLineForm({
                                      ...lineForm,
                                      rate: e.target.value,
                                    })
                                  }
                                  sx={{ width: 100 }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                $
                                {(
                                  (parseFloat(lineForm.quantity) || 0) *
                                  (parseFloat(lineForm.rate) || 0)
                                ).toFixed(2)}
                              </TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  onClick={handleSaveEdit}
                                >
                                  <Save fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={handleCancelEdit}
                                >
                                  <Cancel fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell>
                                {line.description}
                                {line.type === "call-summary" && (
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    display="block"
                                  >
                                    (Call Summary)
                                  </Typography>
                                )}
                                {line.type === "third-party" && (
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    display="block"
                                  >
                                    (Third-Party)
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                {line.quantity || 1}
                              </TableCell>
                              <TableCell align="right">
                                ${(line.rate || 0).toFixed(2)}
                              </TableCell>
                              <TableCell align="right">
                                ${(line.amount || 0).toFixed(2)}
                              </TableCell>
                              {isEditable && (
                                <TableCell align="center">
                                  {canEdit && (
                                    <>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleStartEdit(line)}
                                      >
                                        <Edit fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          setDeleteLineId(line._id)
                                        }
                                      >
                                        <Delete fontSize="small" />
                                      </IconButton>
                                    </>
                                  )}
                                </TableCell>
                              )}
                            </>
                          )}
                        </TableRow>
                      );
                    })}

                    {/* Add new line row */}
                    {addingLine && (
                      <TableRow sx={{ backgroundColor: "#f9f9f9" }}>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="Description"
                            value={lineForm.description}
                            onChange={(e) =>
                              setLineForm({
                                ...lineForm,
                                description: e.target.value,
                              })
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={lineForm.quantity}
                            onChange={(e) =>
                              setLineForm({
                                ...lineForm,
                                quantity: e.target.value,
                              })
                            }
                            sx={{ width: 80 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={lineForm.rate}
                            onChange={(e) =>
                              setLineForm({ ...lineForm, rate: e.target.value })
                            }
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          $
                          {(
                            (parseFloat(lineForm.quantity) || 0) *
                            (parseFloat(lineForm.rate) || 0)
                          ).toFixed(2)}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={handleSaveAdd}
                          >
                            <Save fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={handleCancelEdit}
                          >
                            <Cancel fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>

              {/* Tax breakdown */}
              <Box
                display="flex"
                justifyContent="flex-end"
                gap={3}
              >
                <Box>
                  <Typography variant="body2">Subtotal:</Typography>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                  >
                    ${(invoice.subtotal || 0).toFixed(2)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2">Tax (13%):</Typography>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                  >
                    ${(invoice.taxAmount || 0).toFixed(2)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2">Total:</Typography>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                  >
                    ${(invoice.total || 0).toFixed(2)}
                  </Typography>
                </Box>
              </Box>

              {invoice.notes && (
                <Box>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                  >
                    Notes
                  </Typography>
                  <Typography variant="body2">{invoice.notes}</Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Tab 1: Third-Party Invoices */}
          {activeTab === 1 && (
            <Box
              display="flex"
              flexDirection="column"
              gap={2}
            >
              <Typography variant="h6">
                Available Third-Party Invoice Lines
              </Typography>
              {thirdPartyInvoices.length === 0 ? (
                <Typography color="textSecondary">
                  No third-party invoices found for this client and project.
                </Typography>
              ) : (
                <>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                      <TableRow>
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={
                              selectedThirdPartyLines.size > 0 &&
                              selectedThirdPartyLines.size ===
                                thirdPartyInvoices.length
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                const newSet = new Set(
                                  thirdPartyInvoices.map((_, i) => i),
                                );
                                setSelectedThirdPartyLines(newSet);
                              } else {
                                setSelectedThirdPartyLines(new Set());
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell align="center">Tax Exempt</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {thirdPartyInvoices.map((line, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={selectedThirdPartyLines.has(idx)}
                              onChange={(e) => {
                                const newSet = new Set(selectedThirdPartyLines);
                                if (e.target.checked) {
                                  newSet.add(idx);
                                } else {
                                  newSet.delete(idx);
                                }
                                setSelectedThirdPartyLines(newSet);
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {line.category?.name || "Unknown"}
                          </TableCell>
                          <TableCell>{line.description}</TableCell>
                          <TableCell align="right">
                            ${line.amount.toFixed(2)}
                          </TableCell>
                          <TableCell align="center">
                            {line.taxExempt ? "✓" : ""}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Button
                    variant="contained"
                    onClick={handleCopyThirdPartyLines}
                    disabled={selectedThirdPartyLines.size === 0}
                  >
                    Copy Selected Lines to Invoice
                  </Button>
                </>
              )}
            </Box>
          )}

          {/* Tab 2: Audit History */}
          {activeTab === 2 && <InvoiceAuditHistoryTab invoiceId={invoice.id} />}
        </DialogContent>
        <DialogActions>
          <Box sx={{ flexGrow: 1 }}>
            {invoice.status === "sent" && (
              <Button
                startIcon={<Redo />}
                onClick={handleResendInvoice}
                variant="outlined"
                sx={{ mr: 1 }}
              >
                Resend
              </Button>
            )}
            <Button
              startIcon={<Download />}
              onClick={() => exportCSV(invoice.id)}
              variant="outlined"
              sx={{ mr: 1 }}
            >
              Download CSV
            </Button>
            <Button
              startIcon={<PictureAsPdf />}
              onClick={() => generatePDF(invoice.id)}
              variant="outlined"
            >
              Generate PDF
            </Button>
          </Box>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete line confirmation */}
      <Dialog_YesNo
        open={Boolean(deleteLineId)}
        title="Delete Line?"
        message="Are you sure you want to delete this line item?"
        onYes={handleDeleteLine}
        onNo={() => setDeleteLineId(null)}
      />
    </>
  );
};

export default InvoiceDetailDialog;
